import { type RefObject, useEffect } from 'react';

import {
  WX_LAUNCH_DARKLY_CLIENT_SIDE_ID,
  WX_NAVBAR_URL,
} from '@/config/runtime-env';
import { useAuthContext } from '@/contexts/auth-context';
import { useWxJwtReady } from '@/hooks/use-wx-jwt-ready';
import { domainMeta } from '@/utils/domain-meta';

declare global {
  interface Window {
    WxNavbarComponent?: {
      setConfig: (config: {
        wxServiceBaseUrl?: string;
        stage?: string;
        employerDomain?: string;
        employerBasePath?: string;
        launchDarklyClientSideId?: string;
      }) => void;
      render: (mountId: string, props: Record<string, unknown>) => void;
      /** Modern UMD teardown; preferred over `unmount`. */
      cleanup?: (containerId: string) => void;
      /** @deprecated Older UMD bundles only expose this; kept as a fallback. */
      unmount?: (mountId: string) => void;
    };
  }
}

function umdRemoveMount(
  windowObj: Window['WxNavbarComponent'],
  mountId: string | undefined,
) {
  if (!windowObj || !mountId) return;
  const remove = windowObj.cleanup ?? windowObj.unmount;
  if (typeof remove === 'function') {
    remove(mountId);
  }
}

type UseWxNavbarOptions = {
  mountRef: RefObject<HTMLDivElement | null>;
  renderProps?: Record<string, unknown>;
};

export default function useWxNavbar({
  mountRef,
  renderProps = {},
}: UseWxNavbarOptions): void {
  const { whoami } = useAuthContext();
  const isJwtReady = useWxJwtReady();

  // Pull primitive fields out of `whoami` so the effect's dep list isn't keyed
  // on the whole object identity (which can change on unrelated reducer
  // updates). `renderProps` identity is already filtered upstream by `memo()`
  // on `WxNavbarHost`.
  const accountWxApiUrl = whoami?.account_wx_api_url;
  const accountWxDomain = whoami?.account_wx_domain;
  const hasWhoami = whoami != null;

  // 1) Inject the UMD script once prerequisites are met. Kept separate from the
  // render effect so prop changes do not retrigger script lookup.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasWhoami || !isJwtReady) return;

    const src = WX_NAVBAR_URL;
    const scripts = Array.from(document.getElementsByTagName('script'));
    const alreadyAdded = scripts.some(
      s =>
        s.id === 'wx-navbar-umd' ||
        s.getAttribute('data-wx-navbar-src') === src ||
        s.src === src,
    );

    if (!alreadyAdded && !window.WxNavbarComponent) {
      const script = document.createElement('script');
      script.id = 'wx-navbar-umd';
      script.setAttribute('data-wx-navbar-src', src);
      script.src = src;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [hasWhoami, isJwtReady]);

  // 2) Configure + render the UMD whenever inputs change.
  // The wx-navbar UMD applies subsequent `render(mountId, props)` calls in
  // place against the existing React root, so we deliberately DO NOT call
  // `cleanup` here on dep changes — doing so would unmount the React root and
  // clear the host DOM, producing a visible flicker (and a brief loss of the
  // active product highlight) every time `hostPathname` changes (e.g.
  // navigation, sub-menu clicks, refresh while whoami / feature-access settle).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!hasWhoami || !isJwtReady) return undefined;

    const rootEl = mountRef?.current;
    const mountId = rootEl?.id;
    if (!mountId) return undefined;

    let cancelled = false;

    function configureAndRender() {
      if (cancelled) return;

      if (window.WxNavbarComponent?.setConfig) {
        try {
          window.WxNavbarComponent.setConfig({
            wxServiceBaseUrl: accountWxApiUrl,
            stage: domainMeta.environment,
            employerDomain: accountWxDomain,
            employerBasePath: '',
            launchDarklyClientSideId: WX_LAUNCH_DARKLY_CLIENT_SIDE_ID,
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxNavbarComponent.setConfig failed', e);
        }
      }

      if (window.WxNavbarComponent?.render && mountId) {
        try {
          window.WxNavbarComponent.render(mountId, renderProps);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WxNavbarComponent.render failed', e);
        }
      }
    }

    if (
      window.WxNavbarComponent &&
      typeof window.WxNavbarComponent.render === 'function'
    ) {
      configureAndRender();
      return undefined;
    }

    const onLoaded = () => configureAndRender();
    window.addEventListener('WxNavbarComponent:loaded', onLoaded);
    const poll = setInterval(() => {
      if (
        window.WxNavbarComponent &&
        typeof window.WxNavbarComponent.render === 'function'
      ) {
        clearInterval(poll);
        configureAndRender();
      }
    }, 200);

    return () => {
      // Stop pending load wait, but leave the UMD mounted; subsequent renders
      // update it in place via the next effect run.
      cancelled = true;
      clearInterval(poll);
      window.removeEventListener('WxNavbarComponent:loaded', onLoaded);
    };
  }, [
    mountRef,
    hasWhoami,
    isJwtReady,
    accountWxApiUrl,
    accountWxDomain,
    renderProps,
  ]);

  // 3) Tear down the UMD only when the host component unmounts (e.g. user
  // signs out and `WxNavbarHost` is no longer rendered). Capture the host
  // node on mount so the cleanup uses the same DOM reference even if the ref
  // is reassigned by React in the meantime.
  useEffect(() => {
    const rootEl = mountRef?.current;
    return () => {
      if (typeof window === 'undefined') return;
      const mountId = rootEl?.id;
      if (!mountId) return;
      try {
        umdRemoveMount(window.WxNavbarComponent, mountId);
        rootEl.replaceChildren();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WxNavbarComponent cleanup failed', e);
      }
    };
  }, [mountRef]);
}
